import type {RefObject} from 'react'

import {isOwnKeyboardTarget} from '../focus/keys'

/**
 * How long the input has to be quiet before the reader counts as having stopped.
 *
 * Long enough to span the pause between two flicks of one gesture, and short enough that
 * a reader who has come to a halt is answered rather than left waiting. A continuous
 * wheel or trackpad produces events an order of magnitude closer together than this.
 *
 * It is also what a reader waits before their page is even asked for, which is the price
 * of not having it delivered underneath a movement that is still running.
 *
 * Exported for the tests, which have to outlast it to assert that nothing was asked.
 */
export const REST_MS = 150

/**
 * How long input may go on without a pause before it is reported again anyway.
 *
 * Rest is the natural end of a movement, and for a reader it always comes. Not everything
 * that scrolls is a reader, though, and the ones that are not can go on indefinitely: a
 * smooth scroll animating somewhere else on the page, the board's own auto-scroll under a
 * card held at its edge, a panel tailing its own output. Every one of those keeps `scroll`
 * firing far more often than {@link REST_MS}, so a listener waiting for quiet would wait
 * forever and the direction it belongs to could never ask again.
 *
 * Comfortably longer than a fling, so the movement a reader actually makes is still
 * reported twice and no more.
 */
export const MAX_MOVEMENT_MS = 2000

export type ReaderMovementListener = () => void

/**
 * The element a movement has to have happened inside to be this subscriber's.
 *
 * A ref rather than the element, because a sentinel subscribes as it mounts and does not
 * wait for the board around it. It is read when a movement arrives, so a board whose root
 * mounts later is scoped from that point on.
 *
 * A ref holding null is a board with no element to be inside, and hears every movement on
 * the page. That is what a caller assembling a board out of these hooks without
 * `useBoardRoot` gets, and it is what every board got before there was anything to
 * compare a movement against.
 */
export type ReaderMovementScope = RefObject<HTMLElement | null>

/**
 * What the reader is doing, rather than what it moved.
 *
 * A reader who has reached the end of a cell and keeps flicking produces almost no
 * `scroll` events, because there is nowhere left to move: 121 wheel notches over two
 * seconds were measured producing 21 of them. So the input itself is watched instead of
 * its effect -- `wheel` and `touchmove` are the gesture, and the two sets below cover the
 * keys that scroll a container and the pointer that drags a scrollbar.
 *
 * **`scroll` is deliberately not one of them**, though it is the obvious choice and was
 * the first one tried. A scroll event says a container moved and not who moved it, and on
 * this board most of them are not the reader: appending a page changes the content's
 * height, which shifts the scroll position and fires one. Counting those as the reader
 * makes each delivered page authorise the request for the next, which is the runaway this
 * whole mechanism exists to stop -- one fling was measured producing four requests after
 * the reader had already stopped. Everything below is an event a person has to perform.
 */
const GESTURE_EVENTS = ['wheel', 'touchmove'] as const

/**
 * The keys that scroll a container, which are the only keys that are a reader moving.
 *
 * Every other key is somebody typing, and a keystroke counted as movement means a burst
 * of pages -- one per ghost in view, across every board on the page -- each time they
 * pause in a filter box above the board or in an editor inside a card.
 *
 * The board's own arrow, `Home` and `End` navigation is in here, because it scrolls the
 * board to whatever it moves focus to.
 */
const SCROLLING_KEYS: ReadonlySet<string> = new Set([
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
  ' ',
])

const LISTENER_OPTIONS: AddEventListenerOptions = {capture: true, passive: true}

/**
 * One set of listeners for the whole page rather than one per direction. A board draws a
 * sentinel per cell and one per axis, so a subscription that attached its own would put
 * several window listeners on every cell on screen, each of them woken at wheel rate.
 *
 * The listeners hear the whole page, and each subscriber is told only about the movements
 * that happened inside the scope it gave.
 */
const listeners = new Map<ReaderMovementListener, ReaderMovementScope>()

let isMoving = false
let reportedAt = 0
let restTimer: ReturnType<typeof setTimeout> | undefined

/**
 * Every element the movement in progress has been performed on, and every element above
 * those, which is how a subscriber tells a reader moving its own board from a reader doing
 * something else on the page.
 *
 * The whole movement rather than its latest event, because one movement can pass through
 * several places: a card drag ends over an overlay drawn in a portal, a reader flicks a
 * cell and then the page behind it, and a scope that was only asked about the last of
 * those would never hear that the movement it did contain has ended. A movement is
 * reported to everything it touched.
 *
 * Ancestors are recorded as the movement happens rather than being walked when it rests,
 * because what a movement was performed on is often gone by then: the board draws only
 * the cards on screen, so the card under the pointer at the last notch of a fling is
 * recycled by the scrolling that notch caused.
 */
const movedWithin = new Set<Node>()

/**
 * Which pointers are down, and so whose movement is worth listening to.
 *
 * A set rather than a flag, because a reader with two fingers down who lifts one is still
 * dragging with the other, and a flag would stop listening to the movement they are still
 * performing.
 */
const draggingPointers = new Set<number>()

/** Whether the listeners a drag needs are attached. */
let isWatchingDrag = false

/** Records where one movement event happened, and everything it happened inside. */
function noteMovedWithin(target: EventTarget | null): void {
  if (!(target instanceof Node)) return

  for (let node: Node | null = target; node !== null; node = node.parentNode) {
    // This branch has been walked already, and so has everything above it. A reader
    // flicking the same cell over and over pays for the walk once.
    if (movedWithin.has(node)) return

    movedWithin.add(node)
  }
}

function isWithinScope(scope: ReaderMovementScope): boolean {
  const root = scope.current
  if (root === null) return true

  return movedWithin.has(root)
}

function report(): void {
  reportedAt = Date.now()

  // Copied, because being told is what unmounts some of these: a direction that has just
  // been given its last page tears its sentinel down, and a map mutated while it is being
  // walked skips the entry after the one that went.
  for (const [listener, scope] of [...listeners]) {
    // A movement somewhere else on the page is not this board's reader. Unfiltered, a
    // click in the page header or a pause while typing in a filter box reports rest to
    // every sentinel in view -- on this board and on any other beside it -- and each of
    // them asks for a page nobody scrolled towards.
    if (isWithinScope(scope)) listener()
  }
}

function handleRest(): void {
  restTimer = undefined
  isMoving = false
  report()
}

function handleMovement(target: EventTarget | null): void {
  // A movement that has ended is not one to add to: whatever this is begins a new one,
  // and it is answered where it goes rather than where the last one had been.
  if (!isMoving) movedWithin.clear()

  noteMovedWithin(target)

  if (restTimer !== undefined) clearTimeout(restTimer)
  restTimer = setTimeout(handleRest, REST_MS)

  if (!isMoving) {
    isMoving = true
    reportedAt = Date.now()

    return
  }

  // Nothing is reported while the reader is still going. Reporting as they set off was
  // tried and gives back a page they never see: it lands while the movement is still
  // being delivered, and the momentum left in that movement is spent scrolling through
  // the cards it just brought. Measured on a real board, a fling that asked at one card
  // left the reader four cards further on, past everything it had fetched for them.
  if (Date.now() - reportedAt < MAX_MOVEMENT_MS) return

  // Unless it has been going for longer than a reader's movement lasts, in which case
  // something that is not a reader is scrolling and waiting for it to stop would mean
  // waiting forever. It is called stopped rather than merely reported, because a listener
  // that holds its question back while the reader is going would otherwise hold it back
  // for as long as that scroll ran, which is the stall this exists to prevent. Whatever
  // arrives next begins a fresh movement.
  isMoving = false
  report()
}

function handleGesture(event: Event): void {
  handleMovement(event.target)
}

function handleKeyDown(event: KeyboardEvent): void {
  // A control that answers the key itself scrolls nothing: an arrow in a filter box moves
  // a caret, and one in a menu moves through the menu. The board asks the same question
  // before it acts on a key of its own, so the two cannot disagree about whose key it is.
  if (isOwnKeyboardTarget(event.target)) return

  // Not a shortcut and not a command: these are the keys that scroll a container, and
  // what is being read off them is that the reader moved something.
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  if (!SCROLLING_KEYS.has(event.key)) return

  handleMovement(event.target)
}

/**
 * A press is not a movement, and is not reported as one.
 *
 * It is where a drag might begin, which is what it is watched for. A press that never
 * moves is a click -- on a card, on a header, on a control a caller drew -- and counting
 * it would answer somebody who opened a card with a page in every ghost on screen.
 */
function handlePointerDown(event: PointerEvent): void {
  draggingPointers.add(event.pointerId)
  watchDrag()
}

function handleDragEnd(event: PointerEvent): void {
  draggingPointers.delete(event.pointerId)
  if (draggingPointers.size === 0) stopWatchingDrag()
}

/**
 * A pointer being dragged, which is a reader moving something, as against a pointer being
 * moved across a page, which is a reader moving nothing at all.
 *
 * The release usually says which it is. This covers the release that happened where this
 * listener could not hear it -- outside the window, or swallowed by something that
 * captured the pointer -- after which every stir of the mouse would look like a reader
 * scrolling and a board would page itself under a pointer nobody was pressing.
 */
function handleDragMove(event: PointerEvent): void {
  if (event.buttons === 0) {
    handleDragEnd(event)

    return
  }

  handleMovement(event.target)
}

/**
 * Watched only while a pointer is down, because a pointer that is up is a reader moving
 * the mouse across a page rather than a reader moving anything in it.
 *
 * A drag is the one way to scroll that raises none of the other events at all. A scrollbar
 * dragged with the pointer fires `scroll`, which says nothing about who moved it, and a
 * card dragged to the edge of a cell is scrolled by the board itself. Without these, a
 * reader dragging for several seconds is one the board never hears from, and the page they
 * are owed where they let go is asked for in the middle of the drag instead.
 */
function watchDrag(): void {
  if (isWatchingDrag) return

  isWatchingDrag = true
  window.addEventListener('pointermove', handleDragMove, LISTENER_OPTIONS)
  window.addEventListener('pointerup', handleDragEnd, LISTENER_OPTIONS)
  window.addEventListener('pointercancel', handleDragEnd, LISTENER_OPTIONS)
}

function stopWatchingDrag(): void {
  draggingPointers.clear()
  if (!isWatchingDrag) return

  isWatchingDrag = false
  window.removeEventListener('pointermove', handleDragMove, LISTENER_OPTIONS)
  window.removeEventListener('pointerup', handleDragEnd, LISTENER_OPTIONS)
  window.removeEventListener('pointercancel', handleDragEnd, LISTENER_OPTIONS)
}

function watch(): void {
  for (const type of GESTURE_EVENTS) window.addEventListener(type, handleGesture, LISTENER_OPTIONS)
  window.addEventListener('keydown', handleKeyDown, LISTENER_OPTIONS)
  window.addEventListener('pointerdown', handlePointerDown, LISTENER_OPTIONS)
}

function stopWatching(): void {
  for (const type of GESTURE_EVENTS) window.removeEventListener(type, handleGesture, LISTENER_OPTIONS)
  window.removeEventListener('keydown', handleKeyDown, LISTENER_OPTIONS)
  window.removeEventListener('pointerdown', handlePointerDown, LISTENER_OPTIONS)
  stopWatchingDrag()
}

/**
 * Whether the reader is moving something inside `scope` right now.
 *
 * Scoped for the same reason a report is. A reader flinging a panel elsewhere on the page
 * is no reason to hold back the page a sentinel on this board just asked for, and holding
 * it back would strand that sentinel: the guard a held question sits behind is cleared by
 * rest inside this scope, which is exactly what a movement somewhere else never produces.
 */
export function isReaderMoving(scope: ReaderMovementScope): boolean {
  return isMoving && isWithinScope(scope)
}

/**
 * Reports that the reader has come to rest inside `scope`, once per movement.
 *
 * Rest rather than any part of the movement itself, because a page is only worth
 * delivering to somebody who has stopped to look. One that arrives mid-movement is
 * scrolled straight past by whatever is left of it.
 *
 * A movement that crosses forty screenfuls is one reader going to one place, and is
 * reported once, when they get there.
 *
 * Returns the unsubscribe. The window listeners exist only while somebody is subscribed,
 * so a board with nothing left to load anywhere costs none, and the last unsubscribe
 * leaves no timer behind.
 */
export function subscribeToReaderMovement(listener: ReaderMovementListener, scope: ReaderMovementScope): () => void {
  if (listeners.size === 0) watch()

  listeners.set(listener, scope)

  return () => {
    listeners.delete(listener)
    if (listeners.size > 0) return

    stopWatching()

    if (restTimer !== undefined) clearTimeout(restTimer)
    restTimer = undefined
    // Nobody is listening, so there is no movement to be in the middle of. A state left
    // standing here would be read by whoever subscribes next as their own.
    isMoving = false
    movedWithin.clear()
  }
}
